/**
 * Sidebar icon set, inlined rather than pulled from src/assets/svg because the sidebar design
 * colours its icons via `currentColor` (neutral-600 -> primary-900 on the active nav item).
 * The shared assets hard-code stroke="#C7CBD7", which is why the previous sidebar needed a
 * per-icon `*Active` SCSS override for every single nav entry.
 *
 * All icons share the design's stroke geometry: 24x24 box, no fill, 1.8 stroke unless noted.
 */

type IconProps = { className?: string };

const base = {
  viewBox: "0 0 24 24",
  fill: "none",
  stroke: "currentColor",
  strokeWidth: 1.8,
} as const;

export const ChevronEndIcon: React.FC<IconProps> = ({ className }) => (
  <svg {...base} strokeWidth={2.2} className={className}>
    <path d="m10 7 5 5-5 5" />
  </svg>
);

export const ChevronDownIcon: React.FC<IconProps> = ({ className }) => (
  <svg {...base} strokeWidth={2} className={className}>
    <path d="m6 9 6 6 6-6" />
  </svg>
);

/** Placeholder for an organization with no logo on file. */
export const ImagePlaceholderIcon: React.FC<IconProps> = ({ className }) => (
  <svg {...base} className={className}>
    <rect x="3" y="3" width="18" height="18" rx="4" />
    <circle cx="9" cy="9" r="1.6" />
    <path d="m5 19 5.5-5.5a1.4 1.4 0 0 1 2 0L19 20" />
  </svg>
);

/** Overlaid on the brand tile on hover, to signal the logo can be changed. */
export const CameraIcon: React.FC<IconProps> = ({ className }) => (
  <svg {...base} className={className}>
    <path d="M4 8.5h2.2l1.3-2h9l1.3 2H20a1.5 1.5 0 0 1 1.5 1.5v8A1.5 1.5 0 0 1 20 19.5H4A1.5 1.5 0 0 1 2.5 18v-8A1.5 1.5 0 0 1 4 8.5Z" />
    <circle cx="12" cy="14" r="3.2" />
  </svg>
);

export const HomeIcon: React.FC<IconProps> = ({ className }) => (
  <svg {...base} className={className}>
    <path d="m3 11 9-7 9 7v9a1.5 1.5 0 0 1-1.5 1.5h-15A1.5 1.5 0 0 1 3 20Z" />
  </svg>
);

export const ProjectsIcon: React.FC<IconProps> = ({ className }) => (
  <svg {...base} className={className}>
    <rect x="3" y="3" width="8" height="8" rx="2" />
    <rect x="13" y="13" width="8" height="8" rx="2" />
    <path d="M13 7h8M3 17h8" />
  </svg>
);

export const CustomersIcon: React.FC<IconProps> = ({ className }) => (
  <svg {...base} className={className}>
    <circle cx="9" cy="8" r="3.5" />
    <path d="M2.5 20c.8-3.2 3.4-5 6.5-5s5.7 1.8 6.5 5" />
    <circle cx="17" cy="9" r="2.5" />
    <path d="M16 15.2c2.5.2 4.6 1.7 5.5 4.3" />
  </svg>
);

export const UpdatesIcon: React.FC<IconProps> = ({ className }) => (
  <svg {...base} className={className}>
    <path d="M20 12a8 8 0 1 1-2.3-5.6" />
    <path d="M20 3v4h-4" />
  </svg>
);

export const TransactionsIcon: React.FC<IconProps> = ({ className }) => (
  <svg {...base} className={className}>
    <path d="M4 8h13l-3.2-3.2M20 16H7l3.2 3.2" strokeLinecap="round" />
  </svg>
);

export const BalanceIcon: React.FC<IconProps> = ({ className }) => (
  <svg {...base} className={className}>
    <rect x="2.5" y="6" width="19" height="13" rx="2.5" />
    <path d="M2.5 10h19" />
  </svg>
);

export const SettingsIcon: React.FC<IconProps> = ({ className }) => (
  <svg {...base} className={className}>
    <circle cx="12" cy="12" r="3" />
    <path d="M19 12a7 7 0 0 0-.1-1.2l2-1.6-2-3.4-2.4 1a7 7 0 0 0-2-1.2L14 3h-4l-.5 2.6a7 7 0 0 0-2 1.2l-2.4-1-2 3.4 2 1.6A7 7 0 0 0 5 12c0 .4 0 .8.1 1.2l-2 1.6 2 3.4 2.4-1a7 7 0 0 0 2 1.2L10 21h4l.5-2.6a7 7 0 0 0 2-1.2l2.4 1 2-3.4-2-1.6c.1-.4.1-.8.1-1.2Z" />
  </svg>
);

export const ProfileIcon: React.FC<IconProps> = ({ className }) => (
  <svg {...base} className={className}>
    <circle cx="12" cy="8" r="4" />
    <path d="M4.5 20.5c1-3.8 4-5.5 7.5-5.5s6.5 1.7 7.5 5.5" />
  </svg>
);

export const LogoutIcon: React.FC<IconProps> = ({ className }) => (
  <svg {...base} className={className}>
    <path d="M15 4h4a1.5 1.5 0 0 1 1.5 1.5v13A1.5 1.5 0 0 1 19 20h-4" />
    <path d="m10 8-4 4 4 4M6 12h10" />
  </svg>
);
